import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { RolesService, type CreateRoleDto, type Module } from '../../services/security/roles.service';
import { NotificationToast } from '../ui/NotificationToast';
import { Shield, CheckCircle, XCircle, Settings, Users, Eye, Grid3x3, Search } from 'lucide-react';

const createRoleSchema = z.object({
  nombre: z.string().min(1, 'Nombre es requerido').min(3, 'Mínimo 3 caracteres'),
  descripcion: z.string().optional(),
  moduloIds: z.array(z.string()).min(1, 'Debe seleccionar al menos un módulo')
});

type CreateRoleFormData = z.infer<typeof createRoleSchema>;

interface CreateRoleDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateRoleDrawer: React.FC<CreateRoleDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createdRole, setCreatedRole] = useState<any>(null);
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);
  
  // Estados para módulos
  const [modules, setModules] = useState<Module[]>([]);
  const [isLoadingModules, setIsLoadingModules] = useState(false);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [moduleSearchTerm, setModuleSearchTerm] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue
  } = useForm<CreateRoleFormData>({
    resolver: zodResolver(createRoleSchema),
    defaultValues: {
      moduloIds: []
    }
  });

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      loadModules();
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
      setTimeout(() => {
        setIsLoading(false);
        setErrorMessage(null);
        setCreatedRole(null);
        setSelectedModules([]);
        setModuleSearchTerm('');
        reset();
      }, 300);
    }
  }, [isOpen, reset]);

  const loadModules = async () => {
    setIsLoadingModules(true);
    try {
      const modulesData = await RolesService.getAvailableModules();
      // Verificar que modulesData sea un array antes de usar filter
      const moduleArray = Array.isArray(modulesData) ? modulesData : [];
      setModules(moduleArray.filter(module => module.activo)); // Solo módulos activos
    } catch (error) {
      console.error('Error al cargar módulos:', error);
      setErrorMessage('Error al cargar los módulos disponibles');
    } finally {
      setIsLoadingModules(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const onSubmit = async (data: CreateRoleFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      const roleData: CreateRoleDto = {
        nombre: data.nombre,
        descripcion: data.descripcion,
        moduloIds: selectedModules
      };

      const result = await RolesService.createRole(roleData);
      
      setCreatedRole({
        id: result.id,
        nombre: result.nombre,
        descripcion: result.descripcion,
        modulosAsignados: selectedModules.length
      });

      setNotificationConfig({
        title: '¡Rol creado!',
        message: `El rol ${result.nombre} fue creado exitosamente.`,
        type: 'success'
      });
      setShowNotification(true);

      setTimeout(() => {
        handleClose();
      }, 2000);
      
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error('Error al crear rol:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido al crear rol';
      setErrorMessage(errorMsg);
      
      setNotificationConfig({
        title: 'Error al crear rol',
        message: errorMsg,
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    handleClose();
  };

  const getModuleIcon = (iconName: string) => {
    const icons: { [key: string]: React.ComponentType<any> } = {
      'settings': Settings,
      'users': Users,
      'shield': Shield,
      'eye': Eye,
      'grid': Grid3x3
    };
    
    return icons[iconName] || Settings;
  };

  const toggleModule = (moduleId: string) => {
    const newSelected = selectedModules.includes(moduleId)
      ? selectedModules.filter(id => id !== moduleId)
      : [...selectedModules, moduleId];
    
    setSelectedModules(newSelected);
    setValue('moduloIds', newSelected);
  };

  const filteredModules = modules.filter(module =>
    module.nombre.toLowerCase().includes(moduleSearchTerm.toLowerCase()) ||
    (module.descripcion && module.descripcion.toLowerCase().includes(moduleSearchTerm.toLowerCase()))
  );

  if (!isOpen) return null;

  return (
    <div className="fixed left-0 right-0 bottom-0 top-0 z-[60] overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        <section className={`absolute right-0 top-0 h-full w-full max-w-4xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-medium text-gray-900">
                  Crear Nuevo Rol
                </h2>
                <button
                  onClick={handleClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {errorMessage && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <svg className="w-5 h-5 text-red-400 mr-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-800 text-xs">{errorMessage}</p>
                  </div>
                </div>
              )}

              {!createdRole ? (
                <>
                  {/* Instrucciones */}
                  <div className="mb-6">
                    <h3 className="text-xs font-medium text-gray-900 mb-3">
                      Registro de nuevo rol
                    </h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <ul className="text-xs text-blue-800 space-y-1">
                        <li>• Completa todos los campos obligatorios marcados con (*)</li>
                        <li>• El nombre del rol debe ser único en el sistema</li>
                        <li>• Selecciona los módulos que tendrá acceso este rol</li>
                        <li>• Los permisos se pueden modificar posteriormente</li>
                      </ul>
                    </div>
                  </div>

                  {/* Formulario */}
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Información básica */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-900 mb-3">
                        Información Básica
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Nombre del Rol *
                          </label>
                          <input
                            type="text"
                            {...register('nombre')}
                            placeholder="Ej: Administrador, Editor, Supervisor"
                            className={`w-full px-4 py-3 border rounded-lg transition-all ${
                              errors.nombre
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                            } focus:ring-4 focus:outline-none`}
                          />
                          {errors.nombre && (
                            <p className="mt-1 text-xs text-red-500">{errors.nombre.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Descripción
                          </label>
                          <textarea
                            {...register('descripcion')}
                            placeholder="Describe las responsabilidades y alcance de este rol"
                            rows={3}
                            className={`w-full px-4 py-3 border rounded-lg transition-all ${
                              errors.descripcion
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                            } focus:ring-4 focus:outline-none resize-none`}
                          />
                          {errors.descripcion && (
                            <p className="mt-1 text-xs text-red-500">{errors.descripcion.message}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Selección de Módulos */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-900 mb-3">
                        Módulos del Sistema
                      </h3>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Módulos Asignados *
                        </label>
                        
                        {isLoadingModules ? (
                          <div className="flex items-center justify-center py-8 border border-gray-200 rounded-lg bg-gray-50">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                            <span className="ml-2 text-xs text-gray-500">Cargando módulos...</span>
                          </div>
                        ) : (
                          <>
                            {/* Buscador de módulos */}
                            <div className="mb-4">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                  type="text"
                                  placeholder="Buscar módulos..."
                                  value={moduleSearchTerm}
                                  onChange={(e) => setModuleSearchTerm(e.target.value)}
                                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none text-xs"
                                />
                              </div>
                            </div>

                            {/* Lista de módulos */}
                            <div className="space-y-2 max-h-80 overflow-y-auto border border-gray-200 rounded-lg p-4">
                              {filteredModules.map((module) => {
                                const IconComponent = getModuleIcon(module.icono);
                                const isSelected = selectedModules.includes(module.id);
                                
                                return (
                                  <div
                                    key={module.id}
                                    onClick={() => toggleModule(module.id)}
                                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                                      isSelected
                                        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-500/20'
                                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/30'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center">
                                        <div className={`p-2 rounded-lg mr-3 ${
                                          isSelected ? 'bg-purple-100' : 'bg-gray-100'
                                        }`}>
                                          <IconComponent className={`w-5 h-5 ${
                                            isSelected ? 'text-purple-600' : 'text-gray-600'
                                          }`} />
                                        </div>
                                        <div>
                                          <h4 className={`font-medium ${
                                            isSelected ? 'text-purple-900' : 'text-gray-900'
                                          }`}>
                                            {module.nombre}
                                          </h4>
                                          {module.descripcion && (
                                            <p className={`text-xs ${
                                              isSelected ? 'text-purple-700' : 'text-gray-600'
                                            }`}>
                                              {module.descripcion}
                                            </p>
                                          )}
                                          <p className={`text-xs ${
                                            isSelected ? 'text-purple-600' : 'text-gray-500'
                                          }`}>
                                            {module.permisos?.length || 0} permiso(s) disponible(s)
                                          </p>
                                        </div>
                                      </div>
                                      <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                                        isSelected
                                          ? 'border-purple-500 bg-purple-500'
                                          : 'border-gray-300'
                                      }`}>
                                        {isSelected && (
                                          <CheckCircle className="w-4 h-4 text-white" />
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                              
                              {filteredModules.length === 0 && (
                                <div className="text-center py-8 text-gray-500">
                                  <Settings className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                                  <p className="text-xs">
                                    {moduleSearchTerm ? 'No se encontraron módulos' : 'No hay módulos disponibles'}
                                  </p>
                                </div>
                              )}
                            </div>
                            
                            {/* Módulos seleccionados */}
                            {selectedModules.length > 0 && (
                              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-xs font-medium text-green-800">
                                  {selectedModules.length} módulo(s) seleccionado(s)
                                </p>
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {selectedModules.map(moduleId => {
                                    const module = modules.find(m => m.id === moduleId);
                                    return module ? (
                                      <span
                                        key={moduleId}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full"
                                      >
                                        {module.nombre}
                                      </span>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            )}
                            
                            {errors.moduloIds && (
                              <p className="mt-1 text-xs text-red-500">{errors.moduloIds.message}</p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </form>
                </>
              ) : (
                /* Resultados de la creación */
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-xs font-medium text-gray-900 mb-2">
                      Rol creado exitosamente
                    </h3>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Información del rol</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Nombre:</span>
                        <span className="text-xs font-medium text-gray-900">{createdRole.nombre}</span>
                      </div>
                      {createdRole.descripcion && (
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-500">Descripción:</span>
                          <span className="text-xs font-medium text-gray-900">{createdRole.descripcion}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Módulos asignados:</span>
                        <span className="text-xs font-medium text-gray-900">{createdRole.modulosAsignados}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">ID:</span>
                        <span className="text-xs font-mono text-gray-900">{createdRole.id}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex">
                      <svg className="w-5 h-5 text-blue-400 mr-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-xs text-blue-800 font-medium">Rol listo para asignar</p>
                        <p className="text-xs text-blue-700 mt-1">El rol está disponible para ser asignado a usuarios. Los permisos específicos pueden configurarse posteriormente.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              {!createdRole ? (
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 bg-white text-gray-700 border border-gray-300 px-3 py-2 text-xs rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isLoading}
                    className="flex-1 bg-purple-600 text-white px-3 py-2 text-xs rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {isLoading ? 'Creando Rol...' : 'Crear Rol'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleContinue}
                  className="w-full bg-purple-600 text-white px-3 py-2 text-xs rounded-lg hover:bg-purple-700 transition-colors font-medium"
                >
                  Continuar
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
      
      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
          duration={7000}
        />
      )}
    </div>
  );
};

export default CreateRoleDrawer;